<script setup lang="ts">
/**
 * The battle picker: which rules, which battlefield, and the button that goes.
 *
 * It is a dialog rather than two lists in the sidebar because the sidebar had
 * run out of room — seven maps and two modes as stacked radio rows pushed the
 * Deploy button off the bottom of a laptop screen, which is a poor trade for
 * choices most players make once and then leave alone. Collapsing the sidebar to
 * one line and an *In battle* button puts the common case back on screen and
 * gives the uncommon one the space to show what it is choosing between.
 *
 * It is also where anything else that configures a sortie belongs. That is why
 * the sections are siblings under one heading rather than one dialog per
 * question.
 *
 * ## Two tabs, and the tab *is* the choice
 *
 * PvP and missions are two different games, not two rows of one list: one asks
 * *which rules on which of eight battlefields*, the other *which of three
 * scripted fights, and how hard*. Side by side in one scrolling column they read
 * as five things to pick from, and the Battlefield section had to disappear
 * whenever a mission was selected — a list that vanishes is a list somebody has
 * to learn the rule for.
 *
 * So **switching the tab moves the choice with it**. Opening Missions selects a
 * mission; opening Battle clears it. That is the same discipline the launch route
 * follows server-side — a mission's mode and its map are one decision rather than
 * two that have to agree — and it is what makes the Deploy button below always
 * mean the tab you are looking at. The tab the dialog opens on is derived from
 * what is currently chosen, never stored.
 *
 * ## The Deploy button lives here now
 *
 * The garage's own button opens this dialog; this is where the sortie actually
 * starts. One control that both *shows* the battle and *commits* to it, so there
 * is no way to change the map on one screen and press a stale button on another.
 * The refusals live with it for the same reason — an empty tank and a mission the
 * server could not open are both answers to "can I press this", and they belong
 * on the thing being pressed.
 *
 * **Map art is derived from the id** (`ridge` -> `/assets/maps/ridge.webp`), the
 * same convention hull parts use. A map added to `@shared/maps` therefore gets
 * its picture by existing, and there is no second list to keep in step. A map
 * whose art has not been drawn yet falls back to a plain plate rather than a
 * broken image — the same working state `model: null` gives a prop.
 */
import { computed, ref, watch } from 'vue';
import type { MatchMode, MissionDifficulty, MissionKind } from '@shared/protocol';
import type { MissionRun } from '@/net/api';
import {
  MATCH_MODE_BLURBS,
  MATCH_MODE_NAMES,
  MISSION_DIFFICULTIES,
  MISSION_KINDS,
} from '@shared/protocol';
import { MAP_SUMMARIES, MISSION_MAP_IDS, mapById } from '@shared/maps';
import { MISSION_DIFFICULTY_BLURBS, MISSION_DIFFICULTY_NAMES } from './mission-labels';

const props = defineProps<{
  open: boolean;
  mapId: string;
  mode: MatchMode;
  /**
   * The mission chosen, or null for player-versus-player.
   *
   * A **second axis rather than a mission mode in `mode`**, and that is the
   * safety of it rather than a modelling preference. `mode` is what the deploy
   * carries onto the wire, and `MATCH_MODES` is the server's validation list in
   * three places — so a mission id put in there would be refused by the gateway
   * and the player would land in a deathmatch on mission ground, which is bare
   * and has nothing running on it. Keeping the two apart means a mission choice
   * structurally cannot reach the join.
   */
  mission: MissionKind | null;
  difficulty: MissionDifficulty;
  /**
   * Why a mission cannot be started, or null when one can be.
   *
   * Passed in rather than known here, because this dialog is a controlled
   * component and "is there a route that opens this room" is a fact about the
   * server, not about the picker. Stated where the choice is made rather than
   * only on the deploy button somebody has already walked away from.
   */
  missionBlocked?: string | null;
  /**
   * Whether the player has the fuel for a sortie.
   *
   * A courtesy exactly as it was on the garage's own button — the gate is
   * `PlayerService.openSession`, which charges inside the transaction that opens
   * the sortie, and a client that ignored this would still be refused by the
   * socket. It is here rather than on the button that opens this dialog because a
   * player with an empty tank must still be able to look at the maps.
   */
  canDeploy: boolean;
  /** Fuel a sortie costs, stated on the button so it is not a surprise. */
  fuelCost: number | null;
  /**
   * Which missions this account has already beaten, and on which setting.
   *
   * Passed in rather than fetched, because this dialog is a controlled component
   * and the overview it comes from is already loaded by the screen above. A
   * `(kind, difficulty)` pair rather than a flag per mission — beating Convoy
   * Run on Standard says nothing about Veteran, and a card claiming otherwise
   * would be the picker inventing a rule the server does not have.
   */
  completed?: readonly MissionRun[];
}>();

const emit = defineEmits<{
  close: [];
  'update:mapId': [value: string];
  'update:mode': [value: MatchMode];
  'update:mission': [value: MissionKind | null];
  'update:difficulty': [value: MissionDifficulty];
  /** Start the sortie the dialog is currently describing. */
  deploy: [];
}>();

const maps = MAP_SUMMARIES;

/**
 * The three missions, each with the battlefield its mode implies.
 *
 * **No map chooser sits beside them**, and that is the design rather than an
 * omission: a mission's map is forced server-side from its mode, so offering a
 * choice would be offering one the server ignores. The card names the ground
 * instead, and the Battlefield list is on the other tab — which is the tidier
 * half of the tab split: a section that had to *vanish* whenever a mission was
 * picked is now simply somewhere else.
 *
 * Names and blurbs come from `MATCH_MODE_NAMES` / `MATCH_MODE_BLURBS`, which
 * already carry all five modes — a mission is a mode, so there is no second
 * table to keep in step. The art is derived from the map id exactly as every
 * other plate in the garage is, and a mission map with no picture yet falls back
 * to the same plain plate a new map gets.
 */
const missions = MISSION_KINDS.map((kind) => {
  const map = mapById(MISSION_MAP_IDS[kind]);
  return {
    kind,
    name: MATCH_MODE_NAMES[kind],
    blurb: MATCH_MODE_BLURBS[kind],
    mapId: map.id,
    mapName: map.name,
  };
});

/**
 * The tutorial, which has exactly one difficulty.
 *
 * **Not a special case in the list — it is `MISSION_KINDS[0]` like any other
 * card** — but it is a special case for the Difficulty section below, and that
 * section is hidden for it rather than disabled. A row of three buttons where
 * two do nothing is a control that lies; a section that is absent because the
 * mission has nothing to choose is the same decision the Battlefield list makes
 * on this tab.
 *
 * Written as a comparison against the kind rather than as a flag on the entry,
 * because the server enforces it — `ArenaService.tutorial` builds the room at
 * the default and there is no route that takes another — so a second statement
 * of it here could only ever disagree.
 */
const TUTORIAL: MissionKind = 'tutorial';
const graded = computed(() => props.mission !== TUTORIAL);

/**
 * Which difficulties a mission has been beaten on, by kind.
 *
 * Built once from the flat list the server serves, because the cards ask the
 * question per kind and a `find` per card per render is the same lookup written
 * badly.
 */
const beaten = computed(() => {
  const out = new Map<string, Set<string>>();
  for (const run of props.completed ?? []) {
    const set = out.get(run.kind) ?? new Set<string>();
    set.add(run.difficulty);
    out.set(run.kind, set);
  }
  return out;
});

/** Whether this mission has been completed at *any* difficulty. */
function everBeaten(kind: MissionKind): boolean {
  return (beaten.value.get(kind)?.size ?? 0) > 0;
}

/** Whether this mission has been completed at the difficulty now selected. */
function beatenAt(kind: MissionKind, difficulty: MissionDifficulty): boolean {
  return beaten.value.get(kind)?.has(difficulty) === true;
}

/** Difficulty, as three words. The wording lives in `mission-labels.ts`. */
const difficulties = MISSION_DIFFICULTIES.map((id) => ({
  id,
  name: MISSION_DIFFICULTY_NAMES[id],
  blurb: MISSION_DIFFICULTY_BLURBS[id],
}));

type PickerTab = 'battle' | 'missions';

const TABS: Array<{ id: PickerTab; label: string; blurb: string }> = [
  { id: 'battle', label: 'Battle', blurb: 'Against other players and their crews' },
  { id: 'missions', label: 'Missions', blurb: 'Against the machine' },
];

/**
 * Which half of the dialog is showing.
 *
 * **Derived from the chosen sortie on every open, never persisted.** What a
 * player last deployed with is already stored — by the screen above, in the same
 * `localStorage` the map and the mode live in — so a second remembered value
 * could only ever disagree with the first. Opening the dialog on the tab that
 * does *not* describe the battle the Deploy button would start is the one state
 * worth ruling out structurally.
 */
const tab = ref<PickerTab>(props.mission ? 'missions' : 'battle');

/**
 * The mission to restore when the Missions tab is opened with none chosen.
 *
 * Switching to Battle clears the mission — that is what makes the tab and the
 * Deploy button agree — so without this, coming back would land on nothing
 * selected and a Deploy button describing a mission that is not picked. Seeded
 * from the stored choice so a returning player finds the one they played.
 */
const lastMission = ref<MissionKind>(props.mission ?? MISSION_KINDS[0]);
watch(
  () => props.mission,
  (kind) => {
    if (kind) lastMission.value = kind;
  },
);

/**
 * Switching tabs moves the choice, because the tab *is* the choice. See the file
 * comment: the alternative is a dialog whose Deploy button starts a deathmatch
 * while the player is looking at three missions.
 */
function showTab(id: PickerTab): void {
  if (tab.value === id) return;
  tab.value = id;
  if (id === 'missions') {
    if (!props.mission) emit('update:mission', lastMission.value);
  } else if (props.mission) {
    emit('update:mission', null);
  }
}

const chosenMission = computed(() =>
  props.mission ? (missions.find((m) => m.kind === props.mission) ?? null) : null,
);

/** Ids whose art failed to load, so the fallback plate is drawn instead. */
const missingArt = ref(new Set<string>());
function noteMissing(id: string): void {
  // Replaced rather than mutated: a `Set` mutated in place is not reactive.
  missingArt.value = new Set(missingArt.value).add(id);
}
function artFor(id: string): string | null {
  return missingArt.value.has(id) ? null : `/assets/maps/${id}.webp`;
}

const dialog = ref<HTMLElement | null>(null);
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    // The tab follows the choice rather than remembering itself — see `tab`.
    tab.value = props.mission ? 'missions' : 'battle';
    // Focus moves into the dialog so Escape and Tab land somewhere sensible, and
    // so a keyboard player is not left tabbing through the garage behind it.
    requestAnimationFrame(() => dialog.value?.focus());
  },
);

const chosen = computed(() => maps.find((m) => m.id === props.mapId) ?? maps[0]);
</script>

<template>
  <div
    v-if="props.open"
    class="scrim"
    data-test="battle-picker"
    @click.self="emit('close')"
  >
    <div
      ref="dialog"
      class="dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Choose your battle"
      tabindex="-1"
      @keydown.esc="emit('close')"
    >
      <header>
        <h2>Choose your battle</h2>
        <button type="button" class="close" data-test="battle-picker-close" @click="emit('close')">
          Close
        </button>
      </header>

      <div class="tabs" role="tablist" aria-label="What kind of battle">
        <button
          v-for="entry in TABS"
          :key="entry.id"
          type="button"
          role="tab"
          :aria-selected="tab === entry.id"
          :class="{ chosen: tab === entry.id }"
          :data-test="`battle-tab-${entry.id}`"
          @click="showTab(entry.id)"
        >
          <span class="name">{{ entry.label }}</span>
          <span class="blurb">{{ entry.blurb }}</span>
        </button>
      </div>

      <div class="pane">
      <section v-if="tab === 'missions'" class="block">
        <h3>Mission</h3>
        <p class="note">
          Each is fought on ground of its own, chosen by the mission — there is
          nothing to pick here but which one and how hard.
        </p>
        <div class="maps" role="radiogroup" aria-label="Mission">
          <button
            v-for="entry in missions"
            :key="entry.kind"
            type="button"
            role="radio"
            class="map"
            :aria-checked="props.mission === entry.kind"
            :class="{ chosen: props.mission === entry.kind }"
            :data-test="`mission-option-${entry.kind}`"
            @click="emit('update:mission', entry.kind)"
          >
            <span class="shot">
              <img
                v-if="artFor(entry.mapId)"
                :src="artFor(entry.mapId)!"
                :alt="`Overhead view of ${entry.mapName}`"
                loading="lazy"
                decoding="async"
                @error="noteMissing(entry.mapId)"
              />
              <span v-else class="shot-fallback" aria-hidden="true">{{ entry.name.slice(0, 1) }}</span>
            </span>
            <span class="body">
              <span class="name">
                {{ entry.name }}
                <em v-if="entry.mapName !== entry.name">{{ entry.mapName }}</em>
                <em v-if="everBeaten(entry.kind)" class="beaten" :data-test="`mission-complete-${entry.kind}`">
                  complete
                </em>
              </span>
              <span class="blurb">{{ entry.blurb }}</span>
            </span>
          </button>
        </div>

        <template v-if="graded">
        <h3>Difficulty</h3>
        <div class="difficulty" role="radiogroup" aria-label="Difficulty">
          <button
            v-for="d in difficulties"
            :key="d.id"
            type="button"
            role="radio"
            :aria-checked="props.difficulty === d.id"
            :class="{ chosen: props.difficulty === d.id }"
            :data-test="`difficulty-option-${d.id}`"
            @click="emit('update:difficulty', d.id)"
          >
            <span class="name">
              {{ d.name }}
              <em
                v-if="props.mission && beatenAt(props.mission, d.id)"
                class="beaten"
                :data-test="`difficulty-complete-${d.id}`"
              >✓</em>
            </span>
            <span class="blurb">{{ d.blurb }}</span>
          </button>
        </div>
        </template>

        <p v-else class="note" data-test="tutorial-note">
          One setting, and no charge: basic training costs no fuel and pays no
          credits, XP or rank. Replay it whenever you like.
        </p>

        <p v-if="props.mission && props.missionBlocked" class="blocked" data-test="mission-blocked">
          {{ props.missionBlocked }}
        </p>
      </section>

      <section v-if="tab === 'battle'" class="block">
        <h3>Battlefield</h3>
        <div class="maps" role="radiogroup" aria-label="Battlefield">
          <button
            v-for="entry in maps"
            :key="entry.id"
            type="button"
            role="radio"
            class="map"
            :aria-checked="props.mapId === entry.id"
            :class="{ chosen: props.mapId === entry.id }"
            :data-test="`map-option-${entry.id}`"
            @click="emit('update:mapId', entry.id)"
          >
            <span class="shot">
              <img
                v-if="artFor(entry.id)"
                :src="artFor(entry.id)!"
                :alt="`Overhead view of ${entry.name}`"
                loading="lazy"
                decoding="async"
                @error="noteMissing(entry.id)"
              />
              <span v-else class="shot-fallback" aria-hidden="true">{{ entry.name.slice(0, 1) }}</span>
            </span>
            <span class="body">
              <span class="name">
                {{ entry.name }}
                <em v-if="!entry.teamBases" title="No fortified bases — both sides spawn all round">
                  free-for-all
                </em>
              </span>
              <span class="blurb">{{ entry.blurb }}</span>
            </span>
          </button>
        </div>
      </section>

      </div>

      <footer>
        <span v-if="chosenMission" class="summary" data-test="battle-picker-summary">
          {{ chosenMission.name }}
          <template v-if="chosenMission.mapName !== chosenMission.name">
            · {{ chosenMission.mapName }}
          </template>
          <template v-if="graded"> · {{ MISSION_DIFFICULTY_NAMES[props.difficulty] }}</template>
        </span>
        <span v-else class="summary" data-test="battle-picker-summary">
          {{ MATCH_MODE_NAMES[props.mode] }} · {{ chosen.name }}
        </span>

        <button
          type="button"
          class="deploy"
          data-test="deploy"
          :disabled="(graded && !props.canDeploy) || !!props.missionBlocked"
          :title="props.missionBlocked ?? undefined"
          @click="emit('deploy')"
        >
          <template v-if="props.missionBlocked">Mission not yet available</template>
          <template v-else-if="!graded">Start the drills · free</template>
          <template v-else-if="props.canDeploy">
            Deploy
            <em v-if="props.fuelCost !== null">· {{ props.fuelCost }} fuel</em>
          </template>
          <template v-else>Out of fuel</template>
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  /*
   * Above `MusicControl` (z-index 40, mounted after the garage — so at 40 the
   * chip it used to draw painted through this dialog's footer, sitting on the
   * Deploy button at phone sizes), below the settings window and the workbench
   * (both 60) and far below the purchase confirmation (100). The chip is gone and
   * that file now draws only an arena flash, so this particular collision cannot
   * recur — but the ordering against the two 60s and the 100 is what the number
   * is for, and it is unchanged. Same reasoning as `SettingsPanel`'s own scrim,
   * which records the other half of it.
   */
  z-index: 50;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: rgba(6, 8, 11, 0.72);
  backdrop-filter: blur(2px);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(58rem, 100%);
  max-height: min(46rem, 92vh);
  /* The dialog itself does not scroll — `.body` does. The header, the tab strip
     and the footer are the three things that must stay reachable however long
     the map list gets, and the Deploy button is the one that must never be below
     the fold. */
  min-height: 0;
  padding: 1.4rem 1.5rem 1.1rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.5rem;
  background: var(--steel-900, #14181d);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}

.dialog:focus {
  outline: none;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.4rem;
}

h2 {
  margin: 0;
  font-size: 1.15rem;
}

h3 {
  margin: 1rem 0 0.5rem;
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel-400);
}

/*
 * Quiet, because it is no longer the way out of this dialog that matters. It was
 * the accent slab labelled *Done* while closing was the only thing you could do
 * here; the accent belongs to Deploy now, and two accent buttons in one dialog
 * is two things claiming to be the point of it.
 */
.close {
  padding: 0.4rem 1.1rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.25rem;
  background: transparent;
  color: var(--steel-200, #d7dde3);
  font-weight: 600;
}

.close:hover {
  border-color: var(--accent, #E8722C);
  color: var(--accent, #E8722C);
}

/*
 * The two halves of the dialog. A strip of two wide buttons rather than a row of
 * text tabs, because each one has a sentence to say: *against other players* and
 * *against the machine* is the whole difference, and it is the sentence a player
 * needs before they need any of the cards below.
 */
.tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin: 0.5rem 0 0.2rem;
}

.tabs button {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.3rem;
  background: rgba(255, 255, 255, 0.02);
  color: inherit;
  text-align: left;
}

.tabs button.chosen {
  border-color: var(--accent, #E8722C);
  background: rgba(232, 114, 44, 0.12);
}

.tabs .name {
  font-size: 0.95rem;
}

/*
 * The scrolling half. Everything above and below it is fixed, so a long map list
 * moves under the tab strip rather than pushing the Deploy button off the bottom
 * of a laptop screen — which is the failure this dialog was created to fix, one
 * container along.
 *
 * Named `pane` rather than the obvious `body` because `.body` is already the text
 * half of a map card, and a scroll container's rules landed on twenty of them.
 */
.pane {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.difficulty {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.difficulty button,
.map {
  display: flex;
  text-align: left;
  border: 1px solid var(--steel-600);
  border-radius: 0.3rem;
  background: rgba(255, 255, 255, 0.02);
  color: inherit;
}

.difficulty button {
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.6rem 0.75rem;
}

.difficulty button.chosen,
.map.chosen {
  border-color: var(--accent, #E8722C);
  background: rgba(232, 114, 44, 0.12);
}

/*
 * Why the chosen mission cannot be started yet. Stated, not hidden: a card that
 * selects and then silently does nothing is worse than one that says what is
 * missing, and this is the only place in the garage where a choice is offered
 * ahead of the route that honours it.
 */
/* One line under a section heading, where the section needs a sentence rather
   than only a label. Quiet: it is read once. */
.note {
  margin: -0.2rem 0 0.6rem;
  font-size: 0.76rem;
  line-height: 1.4;
  color: var(--steel-400);
}

.blocked {
  margin: 0.7rem 0 0;
  padding: 0.5rem 0.7rem;
  border-left: 2px solid var(--accent, #E8722C);
  border-radius: 0.2rem;
  background: rgba(232, 114, 44, 0.08);
  font-size: 0.76rem;
  line-height: 1.4;
  color: var(--steel-200, #d7dde3);
}

.maps {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15.5rem, 1fr));
  gap: 0.6rem;
}

.map {
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

/* A fixed 16:9 window, so seven cards make even rows whatever each image's own
   proportions turn out to be. */
.shot {
  position: relative;
  display: block;
  aspect-ratio: 16 / 9;
  background: #0e1116;
}

.shot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.shot-fallback {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--steel-600);
}

.body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem 0.7rem 0.7rem;
}

.name {
  font-weight: 600;
  font-size: 0.92rem;
}

.name em {
  margin-left: 0.35rem;
  padding: 0.05rem 0.3rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.15rem;
  font-size: 0.6rem;
  font-style: normal;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.blurb {
  font-size: 0.76rem;
  line-height: 1.35;
  color: var(--steel-400);
}

/*
 * Beaten. Green rather than the accent, because the accent means *this is the
 * one selected* everywhere else in this dialog and a card can be both.
 */
.name em.beaten {
  border-color: #4caf6d;
  color: #4caf6d;
}

footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex: none;
  margin-top: 0.9rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--steel-700, rgba(255, 255, 255, 0.08));
}

.summary {
  font-size: 0.8rem;
  color: var(--steel-400);
}

/* The one control this dialog ends at, so it is the only accent in it. */
.deploy {
  flex: none;
  padding: 0.75rem 2.2rem;
  border: 1px solid var(--accent, #E8722C);
  border-radius: 0.25rem;
  background: var(--accent, #E8722C);
  color: #14181d;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.deploy em {
  font-style: normal;
  font-weight: 500;
  opacity: 0.75;
}

.deploy:disabled {
  border-color: rgba(255, 255, 255, 0.2);
  background: transparent;
  color: var(--steel-400);
}

/*
 * ─── A phone held sideways ────────────────────────────────────────────────────
 *
 * Gated on `.garage.compact` — `GarageScreen`'s single `matchMedia` answer, the
 * discipline `ItemCard.vue` and `DiamondShop.vue` already follow. This dialog is
 * a DOM descendant of `.garage`, so the ancestor needs nothing; Vue stamps the
 * scope attribute on the last compound selector only.
 *
 * Measured before this block at 667×375: the fixed chrome (header, the two
 * sentence-tall tabs, the footer, the scrim's 1.5rem frame) left the scrolling
 * pane ~90px of a 375px screen — the Battle tab showed the Rules row and **not
 * one map card**, so the map list, the thing this dialog exists to offer, was
 * invisible unless somebody thought to scroll a pane that gave no sign of
 * holding more. The Missions tab showed three cards cut mid-art and no
 * Difficulty section.
 *
 * The answer is the garage's own compact idiom twice over: chrome gives up its
 * sentences (the tab blurbs go — *Battle* and *Missions* carry the choice; the
 * blurbs survive on desktop, where they are read once), and the map cards turn
 * ninety degrees — art in a fixed left gutter, text beside it — exactly as
 * `ItemCard.vue`'s mech bands do, so a screenful of pane shows four to five
 * battlefields instead of most of one. Every control keeps the 44px floor.
 */
.garage.compact .scrim {
  padding: 0.4rem;
}

.garage.compact .dialog {
  width: 100%;
  max-height: 100%;
  padding: 0.55rem 0.75rem 0.55rem;
}

.garage.compact header {
  margin-bottom: 0.15rem;
}

.garage.compact h2 {
  font-size: 0.95rem;
}

.garage.compact .close {
  min-height: 44px;
  padding: 0.3rem 1rem;
}

/* One line each: the label is the choice, and the sentence under it was the
   tallest chrome in the dialog. `min-height` rather than height, so a future
   longer label wraps rather than clips. */
.garage.compact .tabs {
  margin: 0.2rem 0 0.1rem;
  gap: 0.4rem;
}

.garage.compact .tabs button {
  min-height: 44px;
  justify-content: center;
  padding: 0.3rem 0.7rem;
}

.garage.compact .tabs .blurb {
  display: none;
}

.garage.compact h3 {
  margin: 0.65rem 0 0.35rem;
}

.garage.compact .difficulty button {
  min-height: 44px;
  padding: 0.45rem 0.65rem;
}

/*
 * The map card turns ninety degrees: a 16:9 shot stacked over its text is
 * ~200px per card, which is a whole screenful of pane for one battlefield. As a
 * band the shot takes a fixed left gutter and stretches to whatever height the
 * text needs — `aspect-ratio: auto` frees it from the desktop 16:9, and the
 * `img` inside is already `object-fit: cover`, so a shorter window is a tighter
 * crop of the same plate rather than a squashed one.
 */
.garage.compact .maps {
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: 0.5rem;
}

.garage.compact .map {
  flex-direction: row;
}

.garage.compact .shot {
  flex: none;
  align-self: stretch;
  width: 6.5rem;
  aspect-ratio: auto;
  min-height: 3.2rem;
}

.garage.compact .map .body {
  min-width: 0;
  padding: 0.4rem 0.6rem 0.45rem;
  gap: 0.1rem;
}

/* A badge wraps as a whole chip or not at all: in the band's ~200px text column
   `FREE-FOR-ALL` broke mid-word into a two-line chip. An inline-block with its
   whitespace pinned drops to the next line in one piece instead. */
.garage.compact .map .name em {
  display: inline-block;
  white-space: nowrap;
}

/* Two lines of blurb, so the bands stay even and a long description cannot make
   one card twice its neighbour's height. Nothing is lost that matters here — a
   blurb's first two lines are what a picker is for, and the summary in the
   footer restates the choice in full. */
.garage.compact .map .blurb {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.garage.compact footer {
  margin-top: 0.5rem;
  padding-top: 0.45rem;
}

/* The summary yields before the button does: the one control this dialog ends
   at keeps its size, and a long mission name truncates beside it. */
.garage.compact .summary {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.garage.compact .deploy {
  min-height: 44px;
  padding: 0.45rem 1.3rem;
  font-size: 0.9rem;
}
</style>
